import axios, { type AxiosResponse } from "axios";
import { authService } from "./auth";

interface DashboardData {
  doctor: any;
  stats: {
    active_consultations: number;
    today_appointments: number;
    pending_reviews: number;
    total_patients_today: number;
  };
  active_consultations: any[];
  today_appointments: any[];
  pending_reviews: any[];
}

interface WhatsAppResponse {
  success: boolean;
  message?: string;
  error?: string;
  verification_code?: string;
  next_step?: string;
  whatsapp_status?: {
    whatsapp_registered: boolean;
    whatsapp_verified: boolean;
    receives_messages: boolean;
    whatsapp_number_masked?: string;
    business_number: string;
    setup_complete: boolean;
  };
}

interface OnboardingResponse {
  success: boolean;
  onboarding: {
    steps: Record<string, any>;
    completed_steps: number;
    total_steps: number;
    is_complete: boolean;
    progress_percentage: number;
  };
  setup_guide: any;
  next_action: any;
}

export class DoctorService {
  private api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || "/api",
    timeout: 30000,
  });

  constructor() {
    // Use auth service interceptors
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("doctor_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("doctor_token");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      },
    );
  }

  // Dashboard APIs
  async getDashboard(): Promise<DashboardData> {
    try {
      const response: AxiosResponse<DashboardData> =
        await this.api.get("/doctor/dashboard");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getBilling(): Promise<any> {
    try {
      const response = await this.api.get("/doctor/billing");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async downloadInvoice(transactionId: string): Promise<Blob> {
    try {
      const response = await this.api.get(
        `/doctor/billing/transactions/${transactionId}/invoice`,
        {
          responseType: "blob",
        },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getPatientDetails(patientId: string): Promise<any> {
    try {
      const response = await this.api.get(`/doctor/patients/${patientId}`);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async sendMessageToPatient(data: {
    patient_id: string;
    thread_id: string;
    message: string;
  }): Promise<any> {
    try {
      const response = await this.api.post("/doctor/chat/send", data);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getChatMessages(
    threadId: string,
    limit = 50,
    offset = 0,
  ): Promise<any> {
    try {
      const response = await this.api.get(`/doctor/chat/${threadId}/messages`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async endConsultation(
    threadId: string,
    data: {
      consultation_notes?: string;
      follow_up_required?: boolean;
      follow_up_days?: number;
    },
  ): Promise<any> {
    try {
      const response = await this.api.post(
        `/doctor/consultations/${threadId}/end`,
        data,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // WhatsApp APIs
  async registerWhatsApp(whatsappNumber: string): Promise<WhatsAppResponse> {
    try {
      const response: AxiosResponse<WhatsAppResponse> = await this.api.post(
        "/doctor/whatsapp/register",
        {
          whatsapp_number: whatsappNumber,
        },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async sendWhatsAppOTP(
    whatsappNumber: string,
    method: string = "whatsapp",
  ): Promise<WhatsAppResponse> {
    try {
      const response: AxiosResponse<WhatsAppResponse> = await this.api.post(
        "/doctor/whatsapp/send-otp",
        {
          whatsapp_number: whatsappNumber,
          method: method,
        },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async verifyWhatsAppOTP(
    whatsappNumber: string,
    otpCode: string,
  ): Promise<WhatsAppResponse> {
    try {
      const response: AxiosResponse<WhatsAppResponse> = await this.api.post(
        "/doctor/whatsapp/verify-otp",
        {
          whatsapp_number: whatsappNumber,
          otp_code: otpCode,
        },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getWhatsAppStatus(): Promise<WhatsAppResponse> {
    try {
      const response: AxiosResponse<WhatsAppResponse> = await this.api.get(
        "/doctor/whatsapp/status",
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Onboarding APIs
  async getOnboardingStatus(): Promise<OnboardingResponse> {
    try {
      const response: AxiosResponse<OnboardingResponse> =
        await this.api.get("/doctor/onboard");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async updateOnboardingStep(step: string, data: any): Promise<any> {
    try {
      const response = await this.api.post(`/doctor/onboard/${step}`, data);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Profile APIs
  async updateAvailability(availability: any[]): Promise<any> {
    try {
      const response = await this.api.post("/doctor/availability", {
        availability,
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async uploadProfileImage(file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("profile_image", file);

      const response = await this.api.post("/doctor/profile/image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async updateNotificationPreferences(preferences: {
    email_notifications?: boolean;
    sms_notifications?: boolean;
    push_notifications?: boolean;
    marketing_emails?: boolean;
  }): Promise<any> {
    try {
      const response = await this.api.post(
        "/doctor/notifications/preferences",
        preferences,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Quick responses
  async getQuickResponses(): Promise<any> {
    try {
      const response = await this.api.get("/doctor/quick-responses");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async saveQuickResponse(category: string, response: string): Promise<any> {
    try {
      const result = await this.api.post("/doctor/quick-responses", {
        category,
        response,
      });
      return result.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Statistics and Analytics
  async getAnalytics(period = "30d"): Promise<any> {
    try {
      const response = await this.api.get("/doctor/analytics", {
        params: { period },
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getEarnings(startDate?: string, endDate?: string): Promise<any> {
    try {
      const params: any = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await this.api.get("/doctor/earnings", { params });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Notifications
  async getNotifications(): Promise<any> {
    try {
      const response = await this.api.get("/doctor/notifications");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async markNotificationRead(notificationId: string): Promise<any> {
    try {
      const response = await this.api.post(
        `/doctor/notifications/${notificationId}/read`,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Appointments
  async getAppointments(date?: string): Promise<any> {
    try {
      const params: any = {};
      if (date) params.date = date;

      const response = await this.api.get("/doctor/appointments", { params });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async updateAppointmentStatus(
    appointmentId: string,
    status: string,
  ): Promise<any> {
    try {
      const response = await this.api.post(
        `/doctor/appointments/${appointmentId}/status`,
        {
          status,
        },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Settings
  async updateSettings(settings: {
    consultation_fee?: number;
    currency?: string;
    auto_reply?: boolean;
    working_hours?: any;
    break_times?: any;
  }): Promise<any> {
    try {
      const response = await this.api.post("/doctor/settings", settings);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getSettings(): Promise<any> {
    try {
      const response = await this.api.get("/doctor/settings");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Security
  async getSecurityEvents(): Promise<any> {
    try {
      const response = await this.api.get("/doctor/security/events");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async updateSecuritySettings(settings: {
    two_factor_enabled?: boolean;
    login_alerts?: boolean;
    session_timeout?: number;
  }): Promise<any> {
    try {
      const response = await this.api.post(
        "/doctor/security/settings",
        settings,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Utility methods
  async testWhatsAppConnection(): Promise<any> {
    try {
      const response = await this.api.post("/doctor/whatsapp/test");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async exportData(type = "consultations", format = "csv"): Promise<any> {
    try {
      const response = await this.api.get("/doctor/export", {
        params: { type, format },
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `doctor_${type}_${new Date().toISOString().split("T")[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: "Data exported successfully" };
    } catch (error: any) {
      throw error;
    }
  }

  // Health check for doctor-specific APIs
  async healthCheck(): Promise<any> {
    try {
      const response = await this.api.get("/doctor/health");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }
}

// Export singleton instance
export const doctorService = new DoctorService();
export default doctorService;
